import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import type { Profile } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, username: string) => Promise<{ error: AuthError | null; needsEmailConfirmation: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Separate function for actual DB fetch
  const fetchProfileFromDB = useCallback(async (userId: string, retries = 2): Promise<Profile | null> => {
    try {
      // Reduced timeout to 10s for better UX
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timeout after 10s')), 10000)
      );
      
      const fetchPromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]) as any;

      if (error) {
        console.error('[fetchProfile] Supabase error fetching profile:', error);
        throw error;
      }
      
      if (!data) {
        console.error('[fetchProfile] No profile data found for user:', userId);
        throw new Error('Profile not found');
      }
      
      // Cache the profile
      localStorage.setItem(`profile_${userId}`, JSON.stringify({
        data,
        cached_at: Date.now()
      }));
      
      console.log('[fetchProfile] ✅ Profile fetched successfully:', data);
      setProfile(data);
      return data;
    } catch (error) {
      console.error('[fetchProfile] ❌ Exception in fetchProfile:', error);
      
      // Retry on timeout or network errors
      if (retries > 0 && error instanceof Error && 
          (error.message.includes('timeout') || error.message.includes('network'))) {
        console.log(`[fetchProfile] Retrying... (${retries} attempts left)`);
        await new Promise(resolve => setTimeout(resolve, 500)); // Shorter wait
        return fetchProfileFromDB(userId, retries - 1);
      }
      
      throw error;
    }
  }, []);

  // Fetch user profile with timeout - optimized with caching
  const fetchProfile = useCallback(async (userId: string, retries = 2): Promise<Profile | null> => {
    console.log('[fetchProfile] Starting profile fetch for user:', userId, `(retries left: ${retries})`);
    
    // Try to get cached profile first (fast path)
    const cachedProfileKey = `profile_${userId}`;
    const cachedProfile = localStorage.getItem(cachedProfileKey);
    if (cachedProfile) {
      try {
        const parsed = JSON.parse(cachedProfile);
        // Use cache if it's less than 5 minutes old
        if (parsed.cached_at && Date.now() - parsed.cached_at < 5 * 60 * 1000) {
          console.log('[fetchProfile] ✅ Using cached profile');
          setProfile(parsed.data);
          // Fetch fresh data in background to update cache
          fetchProfileFromDB(userId).catch(console.error);
          return parsed.data;
        }
      } catch (e) {
        console.warn('[fetchProfile] Failed to parse cached profile:', e);
      }
    }
    
    // Fetch from database
    return fetchProfileFromDB(userId, retries);
  }, [fetchProfileFromDB]);

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;
    let isInitializing = true;
    
    // Safety net: if auth hasn't resolved in 3 seconds, unblock the UI.
    // The auth listener (onAuthStateChange) will still update state when it resolves.
    const safetyTimeout = setTimeout(() => {
      if (isMounted && isInitializing) {
        console.warn('⚠️ Auth init taking >3s — showing app as unauthenticated');
        setLoading(false);
      }
    }, 3000);

    const initializeAuth = async () => {
      try {
        console.log('🔄 Initializing auth state...');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('❌ Error getting session:', sessionError);
        }
        
        if (!isMounted) return;
        
        // No session at all - user is not logged in
        if (!session) {
          console.log('ℹ️ No session found - user not logged in');
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        console.log('✅ Session found for user:', session.user.email);

        // Set user/session immediately (email confirmation check disabled)
        setSession(session);
        setUser(session.user);
        console.log('✅ User authenticated, loading profile...');
        
        // Fetch profile in background (non-blocking)
        fetchProfile(session.user.id).catch((error) => {
          console.error('❌ Failed to load profile on refresh:', error);
          
          // Only sign out if it's a genuine auth error, not just a timeout or network issue
          const isTemporaryError = error instanceof Error && 
            (error.message.includes('timeout') || error.message.includes('network'));
          
          if (!isTemporaryError) {
            console.warn('Non-temporary error during init, signing out');
            // If profile fetch fails with real error, sign out the user and force clear storage
            supabase.auth.signOut().then(() => {
              // Aggressively clear ALL Supabase-related localStorage
              Object.keys(localStorage).forEach(key => {
                if (key.includes('supabase') || key.includes('sb-')) {
                  console.log('🧹 Removing orphaned localStorage key:', key);
                  localStorage.removeItem(key);
                }
              });
              
              if (isMounted) {
                setSession(null);
                setUser(null);
                setProfile(null);
              }
            });
          } else {
            console.warn('Temporary error during init, profile will retry');
            // Profile will be null but user can still use the app
          }
        });
      } catch (error) {
        console.error('❌ Error in auth initialization:', error);
        
        if (!isMounted) return;
        
        // Clear everything on error
        setSession(null);
        setUser(null);
        setProfile(null);
      } finally {
        clearTimeout(safetyTimeout);
        if (isMounted) {
          // ALWAYS set loading to false, no matter what path we took
          console.log('✅ Auth initialization complete, loading = false');
          setLoading(false);
          isInitializing = false;
        }
      }
    };
    
    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[onAuthStateChange] Event:', event, 'Session:', session?.user?.email, 'isInitializing:', isInitializing);
        
        // Allow SIGNED_IN events through even during initialization (user just logged in)
        // Only skip INITIAL_SESSION to prevent duplicate processing
        if (isInitializing && event === 'INITIAL_SESSION') {
          console.log('[onAuthStateChange] Skipping INITIAL_SESSION during initialization');
          return;
        }
        
        if (!isMounted) {
          console.log('[onAuthStateChange] Component unmounted, skipping');
          return;
        }
        
        // Allow authenticated users (email confirmation check disabled)
        if (session?.user) {
          console.log('[onAuthStateChange] Session found, setting user...');
          
          // Set user/session immediately for fast auth
          setSession(session);
          setUser(session.user);
          setLoading(false);
          console.log('[onAuthStateChange] User and session set successfully');
          
          // Fetch profile in background (non-blocking)
          fetchProfile(session.user.id).catch((error) => {
            console.error('[onAuthStateChange] Failed to load profile:', error);
            
            // Only sign out if it's a genuine auth error, not just a timeout or network issue
            const isTemporaryError = error instanceof Error && 
              (error.message.includes('timeout') || error.message.includes('network'));
            
            if (!isTemporaryError) {
              console.warn('[onAuthStateChange] Non-temporary error, signing out');
              // If profile fetch fails with real error, sign out the user and clear storage
              supabase.auth.signOut().then(() => {
                // Clear ALL Supabase-related localStorage
                Object.keys(localStorage).forEach(key => {
                  if (key.includes('supabase') || key.includes('sb-')) {
                    localStorage.removeItem(key);
                  }
                });
                
                if (isMounted) {
                  setSession(null);
                  setUser(null);
                  setProfile(null);
                }
              });
            } else {
              console.warn('[onAuthStateChange] Temporary error, keeping session active');
              // Keep existing session/user on temporary errors
            }
          });
        } else {
          if (!isMounted) return;
          
          console.log('[onAuthStateChange] No session');
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Sign up with email and password
  const signUp = async (email: string, password: string, username: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
            display_name: username,
          },
          emailRedirectTo: `${window.location.origin}/email-verified`,
        },
      });
      // No session back from signUp means the project requires email
      // confirmation before the user can sign in.
      return { error, needsEmailConfirmation: !error && !data.session };
    } catch (error) {
      return { error: error as AuthError, needsEmailConfirmation: false };
    }
  };

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      console.log('[AuthContext.signIn] Starting sign in...');
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error('[AuthContext.signIn] Sign in error:', error);
        return { error };
      }
      
      console.log('[AuthContext.signIn] Sign in response:', data);
      
      // Email confirmation check disabled - allow all users to sign in
      console.log('[AuthContext.signIn] Email confirmation check skipped');
      
      // Verify session was stored
      const { data: sessionCheck } = await supabase.auth.getSession();
      console.log('[AuthContext.signIn] Session check after sign in:', sessionCheck);
      
      // Check localStorage
      const storageKeys = Object.keys(localStorage).filter(key => 
        key.includes('supabase') || key.includes('sb-')
      );
      console.log('[AuthContext.signIn] Storage keys after sign in:', storageKeys);
      
      return { error: null };
    } catch (error) {
      console.error('[AuthContext.signIn] Unexpected error:', error);
      return { error: error as AuthError };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      // Ensure all tabs are signed out and tokens are cleared
      await supabase.auth.signOut({ scope: 'global' } as any);

      // Clear local auth state
      setUser(null);
      setProfile(null);
      setSession(null);

      // Aggressively clear Supabase storage keys and profile cache
      Object.keys(localStorage).forEach(key => {
        if (key.includes('supabase') || key.includes('sb-') || key.includes('profile_')) {
          localStorage.removeItem(key);
        }
      });
      sessionStorage.clear();
    } catch (error) {
      console.error('Error signing out:', error);
      // Force clear state and storage even if supabase call fails
      setUser(null);
      setProfile(null);
      setSession(null);
      Object.keys(localStorage).forEach(key => {
        if (key.includes('supabase') || key.includes('sb-') || key.includes('profile_')) {
          localStorage.removeItem(key);
        }
      });
      sessionStorage.clear();
    }
  };

  // Request password reset
  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  // Update password
  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  // Update profile
  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error('Not authenticated') };

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      // Refresh profile
      await fetchProfile(user.id);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
